import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { CalendarClock, Timer } from "lucide-react";
import { fetchEconCalendar } from "@/lib/api";

const IMPACT = {
  high: { dot: "bg-neg", label: "text-neg", ring: "border-neg/40" },
  medium: { dot: "bg-amber-term", label: "text-amber-term", ring: "border-amber-term/40" },
  low: { dot: "bg-zinc-600", label: "text-zinc-500", ring: "border-zinc-700" },
};
const VERDICT = {
  beat: "text-pos border-pos/40",
  miss: "text-neg border-neg/40",
  "in-line": "text-zinc-400 border-zinc-700",
};

const TZ = "Asia/Dubai"; // UAE / Gulf Standard Time (UTC+4)
const dayKey = (iso) => new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "2-digit", timeZone: TZ });
const hhmm = (iso) => new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: TZ });

function countdown(ts, now) {
  let s = ts * 1000 - now;
  if (s <= 0) return null;
  const d = Math.floor(s / 86400000); s -= d * 86400000;
  const h = Math.floor(s / 3600000); s -= h * 3600000;
  const m = Math.floor(s / 60000);
  if (d > 0) return `in ${d}d ${h}h`;
  if (h > 0) return `in ${h}h ${m}m`;
  return m > 0 ? `in ${m}m` : "now";
}

function Toggle({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border transition-colors ${
        active ? "border-amber-term/60 text-amber-term bg-amber-500/10" : "border-zinc-700 text-zinc-500 hover:text-zinc-300"
      }`}
    >
      {children}
    </button>
  );
}

export default function CalendarCard() {
  const [usdOnly, setUsdOnly] = useState(false);
  const [highOnly, setHighOnly] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const q = useQuery({
    queryKey: ["econ-calendar"],
    queryFn: () => fetchEconCalendar(undefined, "high,medium"),
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });

  let events = q.data?.events || [];
  if (usdOnly) events = events.filter((e) => e.currency === "USD");
  if (highOnly) events = events.filter((e) => e.impact.toLowerCase() === "high");
  events = events.slice(0, 16);

  // next imminent (first future) event — highlight only this one
  const nextEvent = events.find((e) => e.ts * 1000 > now);

  const groups = [];
  for (const e of events) {
    const k = dayKey(e.datetime_utc);
    let g = groups.find((x) => x.k === k);
    if (!g) groups.push((g = { k, items: [] }));
    g.items.push(e);
  }

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="calendar-card">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 whitespace-nowrap">
          <CalendarClock size={11} strokeWidth={1.5} className="text-amber-term" /> Economic Calendar
          <span className="text-[9px] text-zinc-600 tracking-wider">GST</span>
        </span>
        <span className="flex items-center gap-1.5">
          <Toggle active={usdOnly} onClick={() => setUsdOnly((v) => !v)} testid="cal-toggle-usd">USD</Toggle>
          <Toggle active={highOnly} onClick={() => setHighOnly((v) => !v)} testid="cal-toggle-high">High</Toggle>
        </span>
      </div>

      {q.isLoading ? (
        <div className="h-[90px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">Loading calendar…</div>
      ) : q.isError || events.length === 0 ? (
        <div className="h-[90px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest" data-testid="cal-empty">No matching releases</div>
      ) : (
        <div className="max-h-[340px] overflow-y-auto divide-y divide-zinc-900">
          {groups.map((g) => (
            <div key={g.k} data-testid={`cal-day-${g.k.replace(/[ ,]/g, "-")}`}>
              <div className="px-3 py-1 bg-zinc-900/40 text-[9px] uppercase tracking-widest text-zinc-500 sticky top-0">{g.k}</div>
              {g.items.map((e, i) => {
                const imp = IMPACT[e.impact.toLowerCase()] || IMPACT.low;
                const printed = !!e.actual;
                const isNext = e === nextEvent;
                const cd = isNext ? countdown(e.ts, now) : null;
                return (
                  <div
                    key={i}
                    data-testid="cal-event"
                    className={`px-3 py-1.5 flex items-center gap-2 text-[11px] ${isNext ? "bg-amber-500/[0.07] border-l-2 border-amber-term" : ""}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${imp.dot}`} title={e.impact} />
                    <span className="tabular-nums text-zinc-500 w-10 shrink-0">{hhmm(e.datetime_utc)}</span>
                    <span className={`text-[9px] font-semibold px-1 py-0.5 border ${imp.ring} ${imp.label} shrink-0 w-9 text-center`}>{e.currency}</span>
                    <span className="text-zinc-300 truncate flex-1" title={e.title}>{e.title}</span>
                    {cd && (
                      <span className="text-[9px] uppercase tracking-wider text-amber-term flex items-center gap-0.5 shrink-0" data-testid="cal-countdown">
                        <Timer size={9} strokeWidth={2} />{cd}
                      </span>
                    )}
                    {printed && e.verdict ? (
                      <span className={`text-[9px] uppercase tracking-wider px-1 py-0.5 border shrink-0 ${VERDICT[e.verdict] || VERDICT["in-line"]}`} data-testid="cal-verdict" title={`actual ${e.actual} vs consensus ${e.forecast}`}>
                        {e.verdict}
                      </span>
                    ) : printed ? (
                      <span className="tabular-nums text-zinc-200 shrink-0">{e.actual}<span className="text-zinc-600"> /{e.forecast || "—"}</span></span>
                    ) : (
                      !cd && <span className="tabular-nums text-zinc-600 shrink-0">{e.forecast || "—"}<span className="text-zinc-700"> /{e.previous || "—"}</span></span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
